import { localStorageGet } from '@alexis/helpers/localStorage';
import { getSiteConfigByHostname } from '@alexis/helpers/site';
import { isEmpty } from 'lodash-es';
import type { Dispatch } from 'redux';
import type { NextObserver } from 'rxjs';

import { PRICE_OPTIONS_LOCAL_STORAGE_KEY } from '@constants/localStorage';

import { uniqBy, notUndefinedAndNull } from '@helpers/array';
import { sortHotelRates } from '@helpers/hotel';

import {
  changeHotelMetaSearch,
  changeRateProviders,
  changeAmenitiesFilter,
  changeBookingOptionsFilter,
  changeBrandsFilter,
  changeChainsFilter,
  changeDistrictsFilter,
  changeHotelLandmarks,
  changePropertyTypesFilter,
  changeRateAmenitiesFilter,
  changeReviewerGroupsFilter,
  changeStarsFilter,
  changeHotelViewModels,
  changeSponsoredAdHotelViewModels,
  changeCities,
} from '@redux/actions/hotelMetaSearchActions';
import {
  changeIsIncludingTaxOrFee,
  changeShowTotalPrice,
} from '@redux/actions/priceOptionsActions';

import { HotelLandmark } from '@wegoTypes/hotels/hotelLandmarks';
import { HotelMetaSearchFilter } from '@wegoTypes/hotels/hotelMetaSearchFilter';
import { HotelViewModel } from '@wegoTypes/hotels/hotelViewModel';
import {
  District,
  HotelAmenity,
  HotelSearchResultBrand,
  HotelSearchResultChain,
  HotelSearchResultStar,
  PropertyType,
} from '@wegoTypes/hotels/metasearch/hotelEntity';
import { HotelRateAmenity } from '@wegoTypes/hotels/metasearch/hotelRateAmenity';
import { HotelSearchResult } from '@wegoTypes/hotels/metasearch/hotelSearchResult';
import { HotelSearchResultHotel } from '@wegoTypes/hotels/metasearch/hotelSearchResultHotel';
import { MetasearchHotelRateProvider } from '@wegoTypes/hotels/metasearch/metasearchHotelRateProvider';
import {
  MetasearchRate,
  MetasearchSponsoredAdRate,
} from '@wegoTypes/hotels/metasearch/metasearchRate';
import { RateViewModel } from '@wegoTypes/hotels/rateViewModel';

export function hotelSearchResultObserver(
  dispatch: Dispatch,
  priceOptions: PriceOptions,
  complete?: () => void,
  error?: (err: any) => void,
): NextObserver<HotelSearchResult> {
  // Merge and distinct hotels from each push.
  let previousHotels: Array<HotelSearchResultHotel> = [];
  function mergeAndDistinctHotels(
    hotels: Array<HotelSearchResultHotel>,
  ): Array<HotelSearchResultHotel> {
    const distinctHotels = uniqBy<HotelSearchResultHotel>([...hotels, ...previousHotels], 'id');

    previousHotels = distinctHotels;
    return distinctHotels;
  }

  // Merge and distinct rate providers from each push.
  let previousRateProviders: Array<MetasearchHotelRateProvider> = [];
  function mergeAndDistinctRateProviders(
    rateProviders: Array<MetasearchHotelRateProvider>,
  ): Array<MetasearchHotelRateProvider> {
    const distinctRateProviders = uniqBy<MetasearchHotelRateProvider>(
      [...rateProviders, ...previousRateProviders],
      'id',
    );

    dispatch(changeRateProviders(distinctRateProviders));
    previousRateProviders = distinctRateProviders;
    return distinctRateProviders;
  }

  // Merge and distinct hotel amenities from each push.
  let previousHotelAmenities: Array<HotelAmenity> = [];
  function mergeAndDistinctAmenities(hotelAmenities: Array<HotelAmenity>): Array<HotelAmenity> {
    const distinctHotelAmenities = uniqBy<HotelAmenity>(
      [...hotelAmenities, ...previousHotelAmenities],
      'id',
    );

    previousHotelAmenities = distinctHotelAmenities;
    return distinctHotelAmenities;
  }

  // Merge and distinct property types from each push.
  let previousPropertyTypes: Array<PropertyType> = [];
  function mergeAndDistinctPropertyTypes(propertyTypes: Array<PropertyType>): Array<PropertyType> {
    const distinctPropertyTypes = uniqBy<PropertyType>(
      [...propertyTypes, ...previousPropertyTypes],
      'id',
    );

    previousPropertyTypes = distinctPropertyTypes;
    return distinctPropertyTypes;
  }

  // Merge and distinct districts from each push.
  let previousDistricts: Array<District> = [];
  const mergeAndDistinctDistricts = (districts: Array<District>): Array<District> => {
    const distinctDistricts = uniqBy<District>([...districts, ...previousDistricts], 'id');

    previousDistricts = distinctDistricts;
    return distinctDistricts;
  };

  // Merge and distinct stars filter from each push.
  let previousStarsFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctStarsFilter(
    filters: Array<{ code: string; count: number }>,
    stars: Array<HotelSearchResultStar>,
  ): void {
    const starsFilter = filters
      .map((filter) => {
        const id = parseInt(filter.code);
        const star = stars.find((star) => star.id === id);

        if (!!star) {
          return {
            id: id,
            name: star.name,
            count: filter.count,
          };
        }
      })
      .filter(notUndefinedAndNull);

    const distinctStarsFilter = uniqBy<HotelMetaSearchFilter>(
      [...starsFilter, ...previousStarsFilter],
      'id',
    ).sort((a, b) => a.id - b.id);

    dispatch(changeStarsFilter(distinctStarsFilter));
    previousStarsFilter = distinctStarsFilter;
  }

  // Merge and distinct reviewer groups filter from each push.
  let previousReviewerGroupsFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctReviewerGroupsFilter(
    filters: Array<{ code: string; count: number }>,
  ): void {
    const reviewerGroupsFilter = filters
      .map((filter, index) => {
        return { id: index, name: filter.code, count: filter.count };
      })
      .sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        if (b.name.toLowerCase() > a.name.toLowerCase()) {
          return -1;
        }
        return 0;
      });

    const distinctReviewerGroupsFilter = uniqBy<HotelMetaSearchFilter>(
      [...reviewerGroupsFilter, ...previousReviewerGroupsFilter],
      'name',
    );

    dispatch(changeReviewerGroupsFilter(distinctReviewerGroupsFilter));
    previousReviewerGroupsFilter = distinctReviewerGroupsFilter;
  }

  // Merge and distinct rate amenities filter from each push.
  let previousRateAmenitiesFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctRateAmenitiesFilter(
    filters: Array<{ code: string; count: number }>,
    rateAmenities: Array<HotelRateAmenity>,
  ): void {
    const rateAmenitiesFilter = filters
      .map((filter) => {
        const id = parseInt(filter.code);
        const rateAmenity = rateAmenities.find((rateAmenity) => rateAmenity.id === id);

        if (!!rateAmenity) {
          return {
            id: id,
            name: rateAmenity.name,
            count: filter.count,
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        if (b.name.toLowerCase() > a.name.toLowerCase()) {
          return -1;
        }
        return 0;
      });

    const distinctRateAmenitiesFilter = uniqBy<HotelMetaSearchFilter>(
      [...rateAmenitiesFilter, ...previousRateAmenitiesFilter],
      'id',
    );

    dispatch(changeRateAmenitiesFilter(distinctRateAmenitiesFilter));
    previousRateAmenitiesFilter = distinctRateAmenitiesFilter;
  }

  // Merge and distinct property types filter from each push.
  let previousPropertyTypesFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctPropertyTypesFilter(
    filters: Array<{ code: string; count: number }>,
    propertyTypes: Array<PropertyType>,
  ): void {
    const propertyTypesFilter = filters
      .map((filter) => {
        const id = parseInt(filter.code);
        const propertyType = propertyTypes.find((propertyType) => propertyType.id === id);

        if (!!propertyType) {
          return {
            id: id,
            name: propertyType.name,
            count: filter.count,
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        if (b.name.toLowerCase() > a.name.toLowerCase()) {
          return -1;
        }
        return 0;
      });

    const distinctPropertyTypesFilter = uniqBy<HotelMetaSearchFilter>(
      [...propertyTypesFilter, ...previousPropertyTypesFilter],
      'id',
    );

    dispatch(changePropertyTypesFilter(distinctPropertyTypesFilter));
    previousPropertyTypesFilter = distinctPropertyTypesFilter;
  }

  // Merge and distinct districts filter from each push.
  let previousDistrictsFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctDistrictsFilter(
    filters: Array<{ code: string; count: number }>,
    districts: Array<District>,
  ): void {
    const districtsFilter = filters
      .map((filter) => {
        const id = parseInt(filter.code);
        const district = districts.find((district) => district.id === id);

        if (!!district) {
          return {
            id: id,
            name: district.name,
            count: filter.count,
          };
        }
      })
      .filter(notUndefinedAndNull);

    const distinctDistrictsFilter = uniqBy<HotelMetaSearchFilter>(
      [...districtsFilter, ...previousDistrictsFilter],
      'id',
    ).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
    );

    dispatch(changeDistrictsFilter(distinctDistrictsFilter));
    previousDistrictsFilter = distinctDistrictsFilter;
  }

  // Merge and distinct amenities filter from each push.
  let previousAmenitiesFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctAmenitiesFilter(
    filters: Array<{ code: string; count: number }>,
    amenities: Array<HotelAmenity>,
  ): void {
    const amenitiesFilter = filters
      .map((filter) => {
        const id = parseInt(filter.code);
        const amenity = amenities.find((amenity) => amenity.id === id);

        if (!!amenity) {
          return {
            id: id,
            name: amenity.name,
            count: filter.count,
          };
        }
      })
      .filter(notUndefinedAndNull);

    const distinctAmenitiesFilter = uniqBy<HotelMetaSearchFilter>(
      [...amenitiesFilter, ...previousAmenitiesFilter],
      'id',
    );

    const sortedAmenities = filters.map(
      (id, idx) =>
        distinctAmenitiesFilter.find((amenity) => amenity.id.toString() === id.code) ||
        distinctAmenitiesFilter[idx],
    );

    dispatch(changeAmenitiesFilter(sortedAmenities));
    previousAmenitiesFilter = distinctAmenitiesFilter;
  }

  // Merge and distinct chains filter from each push.
  let previousChainsFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctChainsFilter(
    filters: Array<{ code: string; count: number }>,
    chains: Array<HotelSearchResultChain>,
  ): void {
    const chainsFilter = filters
      .map((filter) => {
        const id = parseInt(filter.code);
        const chain = chains.find((chain) => chain.id === id);

        if (!!chain) {
          return {
            id: id,
            name: chain.name,
            count: filter.count,
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        if (b.name.toLowerCase() > a.name.toLowerCase()) {
          return -1;
        }
        return 0;
      });

    const distinctChainsFilter = uniqBy<HotelMetaSearchFilter>(
      [...chainsFilter, ...previousChainsFilter],
      'id',
    );

    dispatch(changeChainsFilter(distinctChainsFilter));
    previousChainsFilter = distinctChainsFilter;
  }

  // Merge and distinct brands filter from each push.
  let previousBrandsFilter: Array<HotelMetaSearchFilter> = [];
  function mergeAndDistinctBrandsFilter(
    filters: Array<{ code: string; count: number }>,
    brands: Array<HotelSearchResultBrand>,
  ): void {
    const brandsFilter = filters
      .map((filter) => {
        const id = parseInt(filter.code);
        const brand = brands.find((brand) => brand.id === id);

        if (!!brand) {
          return {
            id: id,
            name: brand.name,
            count: filter.count,
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        if (b.name.toLowerCase() > a.name.toLowerCase()) {
          return -1;
        }
        return 0;
      });

    const distinctBrandsFilter = uniqBy<HotelMetaSearchFilter>(
      [...brandsFilter, ...previousBrandsFilter],
      'id',
    );

    dispatch(changeBrandsFilter(distinctBrandsFilter));
    previousBrandsFilter = distinctBrandsFilter;
  }

  function mapBookingOptionsFilter(filters: Array<{ code: string; count: number }>): void {
    const bookingOptionsFilter = filters
      .map((filter, index) => {
        return { id: index, name: filter.code, count: filter.count };
      })
      .sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        if (b.name.toLowerCase() > a.name.toLowerCase()) {
          return -1;
        }
        return 0;
      });

    dispatch(changeBookingOptionsFilter(bookingOptionsFilter));
  }

  let previousSearchId = '';
  function updateHotelLandmarks(searchId: string, landmarks: Array<HotelLandmark>) {
    // Update landmarks only when making new search
    if (searchId !== previousSearchId) {
      dispatch(changeHotelLandmarks(landmarks));
    }
    previousSearchId = searchId;
  }

  let previousHotelViewModels: Array<HotelViewModel> = [];
  function createHotelViewModels(
    hotels: Array<HotelSearchResultHotel>,
    amenities: Array<HotelAmenity>,
    districts: Array<District>,
    propertyTypes: Array<PropertyType>,
    rateProviders: Array<MetasearchHotelRateProvider>,
    rateAmenities: Array<HotelRateAmenity>,
    rates: Array<MetasearchRate>,
    scores: { [key: number]: number },
    providerRatesCounts: Array<{ hotelId: number; providerCode: string; ratesCount: number }>,
    socialProofs: Array<{ mostClickedHotelId: number }>,
  ): void {
    const currentHotelViewModels = hotels.map<HotelViewModel>((hotel) => {
      const previousHotelViewModel = previousHotelViewModels.find(
        (previousHotelViewModel) => previousHotelViewModel.hotelId === hotel.id,
      );

      const hotelAmenities = !!previousHotelViewModel
        ? previousHotelViewModel.amenities
        : amenities.filter((amenity) =>
            hotel.amenityIds.some((amenityId) => amenityId === amenity.id),
          );
      const district = !!previousHotelViewModel
        ? previousHotelViewModel.district
        : districts.find((district) => district.id === hotel.districtId);
      const propertyType = !!previousHotelViewModel
        ? previousHotelViewModel.propertyType
        : propertyTypes.find((propertyTypes) => propertyTypes.id === hotel.propertyTypeId);

      const rateViewModels = rates
        .filter((rate) => rate.hotelId === hotel.id)
        .map<RateViewModel>((rate) => {
          const paymentTypeRateAmenities = rate.rateAmenityIds
            .map((rateAmenityId) =>
              rateAmenities.find((rateAmenity) => rateAmenity.id === rateAmenityId),
            )
            .filter(notUndefinedAndNull)
            .filter(
              (rateAmenity) =>
                rateAmenity.type === 'PAYMENT' && rateAmenity.code !== 'NO_CREDIT_CARD_NEEDED',
            ) // rateAmenity.id === 13 is NO_CREDIT_CARD_NEEDED
            .sort((a, b) => a.id - b.id);

          const provider = rateProviders.find((provider) => provider.code === rate.providerCode);

          return {
            handoffUrl: rate.handoffUrl,
            id: rate.id,
            params: rate.params,
            paymentTypeRateAmenities: paymentTypeRateAmenities,
            price: rate.price,
            promos: rate.promos,
            provider: provider!,
            rateAmenityIds: rate.rateAmenityIds,
            usualPrice: rate.usualPrice,
          };
        });

      const mergedRateViewModels = sortHotelRates(
        uniqBy<RateViewModel>(
          [...rateViewModels, ...(!!previousHotelViewModel ? previousHotelViewModel.rates : [])],
          'id',
        ),
        'asc',
        priceOptions,
        null,
        null,
      );

      const filteredProviderRatesCounts = providerRatesCounts
        .filter((providerRatesCount) => providerRatesCount.hotelId === hotel.id)
        .map<{ count: number; providerCode: string }>((providerRatesCount) => {
          return {
            count: providerRatesCount.ratesCount,
            providerCode: providerRatesCount.providerCode,
          };
        });
      const mergedFilteredProviderRatesCounts = [
        ...(!!previousHotelViewModel ? previousHotelViewModel.providerRatesCounts : []),
        ...filteredProviderRatesCounts,
      ];

      const hasSocialProofs = socialProofs
        .map((socialProof) => socialProof.mostClickedHotelId)
        .includes(hotel.id);

      const hotelViewModel: HotelViewModel = {
        amenities: hotelAmenities,
        district: district,
        filteredRates: mergedRateViewModels,
        hotel: hotel,
        hotelId: hotel.id,
        propertyType: propertyType,
        providerRatesCounts: mergedFilteredProviderRatesCounts,
        rates: mergedRateViewModels,
        score: scores[hotel.id] ?? 0,
        ...(hasSocialProofs && { socialProofs }),
      };

      return hotelViewModel;
    });

    const distinctHotelViewModels = uniqBy<HotelViewModel>(
      [...currentHotelViewModels, ...previousHotelViewModels],
      'hotelId',
    );
    dispatch(changeHotelViewModels(distinctHotelViewModels));
    previousHotelViewModels = distinctHotelViewModels;
  }

  function createSponsoredAdHotelViewModels(
    hotels: Array<HotelSearchResultHotel>,
    amenities: Array<HotelAmenity>,
    districts: Array<District>,
    propertyTypes: Array<PropertyType>,
    rateProviders: Array<MetasearchHotelRateProvider>,
    rateAmenities: Array<HotelRateAmenity>,
    rates: Array<MetasearchSponsoredAdRate>,
    scores: { [key: number]: number },
    providerRatesCounts: Array<{ hotelId: number; providerCode: string; ratesCount: number }>,
    socialProofs: Array<{ mostClickedHotelId: number }>,
  ): void {
    const hotelViewModels = rates
      .map<HotelViewModel | undefined>((rate) => {
        const hotel = hotels.find((hotel) => hotel.id === rate.hotelId);

        if (!!hotel) {
          const hotelAmenities = amenities.filter((amenity) =>
            hotel.amenityIds.some((amenityId) => amenityId === amenity.id),
          );
          const district = districts.find((district) => district.id === hotel.districtId);
          const propertyType = propertyTypes.find(
            (propertyTypes) => propertyTypes.id === hotel.propertyTypeId,
          )!;

          const paymentTypeRateAmenities = rate.rateAmenityIds
            .map((rateAmenityId) =>
              rateAmenities.find((rateAmenity) => rateAmenity.id === rateAmenityId),
            )
            .filter(notUndefinedAndNull)
            .filter(
              (rateAmenity) =>
                rateAmenity.type === 'PAYMENT' && rateAmenity.code !== 'NO_CREDIT_CARD_NEEDED',
            ) // rateAmenity.id === 13 is NO_CREDIT_CARD_NEEDED
            .sort((a, b) => a.id - b.id);

          const provider = rateProviders.find((provider) => provider.code === rate.providerCode);

          const rateViewModel: RateViewModel = {
            handoffUrl: rate.handoffUrl,
            id: rate.id,
            params: rate.params,
            paymentTypeRateAmenities: paymentTypeRateAmenities,
            price: rate.price,
            promos: rate.promos,
            provider: provider!,
            rateAmenityIds: rate.rateAmenityIds,
          };

          const filteredProviderRatesCounts = providerRatesCounts
            .filter((providerRatesCount) => providerRatesCount.hotelId === hotel.id)
            .map<{ count: number; providerCode: string }>((providerRatesCount) => {
              return {
                count: providerRatesCount.ratesCount,
                providerCode: providerRatesCount.providerCode,
              };
            });

          const hasSocialProofs = socialProofs
            .map((socialProof) => socialProof.mostClickedHotelId)
            .includes(hotel.id);

          const hotelViewModel: HotelViewModel = {
            amenities: hotelAmenities,
            district: district,
            filteredRates: [rateViewModel],
            hotel: hotel,
            hotelId: hotel.id,
            propertyType: propertyType,
            providerRatesCounts: filteredProviderRatesCounts,
            rates: [rateViewModel],
            score: scores[hotel.id] ?? 0,
            sponsoredAd: {
              brandColor: rate.brandColor,
              brandDescription: rate.brandDescription,
              brandHeader: rate.brandHeader,
              brandLogoUrl: rate.brandLogoUrl,
            },
            ...(hasSocialProofs && { socialProofs }),
          };

          return hotelViewModel;
        }
        return undefined;
      })
      .filter(notUndefinedAndNull);

    dispatch(changeSponsoredAdHotelViewModels(hotelViewModels));
  }

  return {
    next: (value: HotelSearchResult) => {
      const {
        amenities,
        brands,
        cities,
        chains,
        districts,
        filter,
        hotels,
        priceDisplaying,
        landmarks,
        propertyTypes,
        providerRatesCounts,
        providers,
        rateAmenities,
        rates,
        scores,
        search,
        sponsoredAdRates,
        stars,
        socialProofs,
      } = value;
      const priceOptions = localStorageGet<PriceOptions & { isInit?: boolean }>(
        PRICE_OPTIONS_LOCAL_STORAGE_KEY,
      );
      const currentSite = getSiteConfigByHostname(window.location.hostname);

      dispatch(
        changeHotelMetaSearch({
          ...search,
          ...(search.searchType === 'GEO_LOCATION' &&
            !search.city && { city: cities[0], cityName: cities[0].name }),
        }),
      );
      !!cities && dispatch(changeCities(cities));
      !!landmarks && updateHotelLandmarks(search.id, landmarks);

      if (isEmpty(priceOptions) && !!priceDisplaying) {
        const showTotalPrice =
          priceDisplaying.price !== 'FE_DEFAULT' ? priceDisplaying.price === 'TOTAL_STAY' : false;
        const includeTaxOrFee =
          priceDisplaying.totalTax !== 'FE_DEFAULT'
            ? priceDisplaying.totalTax === 'INCLUDED'
            : /(?:AT|AU|DE|GB|NL|NO|US|SV)/i.test(currentSite.countryCode);

        dispatch(changeShowTotalPrice(showTotalPrice));
        dispatch(changeIsIncludingTaxOrFee(includeTaxOrFee));
      }

      const mergedAndDistinctAmenities = mergeAndDistinctAmenities(amenities || []);
      const mergedAndDistinctDistricts = mergeAndDistinctDistricts(districts || []);
      const mergedAndDistinctHotels = mergeAndDistinctHotels(hotels || []);
      const mergedAndDistinctPropertyTypes = mergeAndDistinctPropertyTypes(propertyTypes || []);
      const mergedAndDistinctProviders = mergeAndDistinctRateProviders(providers || []);

      mergeAndDistinctStarsFilter((!!filter && filter.stars) || [], stars || []);
      mergeAndDistinctReviewerGroupsFilter((!!filter && filter.reviewerGroups) || []);
      mergeAndDistinctRateAmenitiesFilter(
        (!!filter && filter.rateAmenities) || [],
        rateAmenities || [],
      );
      mergeAndDistinctPropertyTypesFilter(
        (!!filter && filter.propertyTypes) || [],
        propertyTypes || [],
      );
      mergeAndDistinctDistrictsFilter((!!filter && filter.districts) || [], districts || []);
      mergeAndDistinctAmenitiesFilter((!!filter && filter.amenities) || [], amenities || []);
      mergeAndDistinctChainsFilter((!!filter && filter.chains) || [], chains || []);
      mergeAndDistinctBrandsFilter((!!filter && filter.brands) || [], brands || []);

      mapBookingOptionsFilter((!!filter && filter.bookingOptions) || []);

      createHotelViewModels(
        mergedAndDistinctHotels || [],
        mergedAndDistinctAmenities || [],
        mergedAndDistinctDistricts || [],
        mergedAndDistinctPropertyTypes || [],
        mergedAndDistinctProviders || [],
        rateAmenities || [],
        rates || [],
        scores || {},
        providerRatesCounts || [],
        socialProofs,
      );

      createSponsoredAdHotelViewModels(
        mergedAndDistinctHotels || [],
        mergedAndDistinctAmenities || [],
        mergedAndDistinctDistricts || [],
        mergedAndDistinctPropertyTypes || [],
        mergedAndDistinctProviders || [],
        rateAmenities || [],
        sponsoredAdRates || [],
        scores || {},
        providerRatesCounts || [],
        socialProofs,
      );
    },
    complete,
    error,
  };
}
